import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  FiUsers, FiCalendar, FiBriefcase, FiDollarSign,
  FiTrendingUp, FiFileText, FiBarChart2, FiSettings,
  FiPlus, FiList, FiUser, FiClock, FiCheckSquare,
  FiDownload, FiTarget, FiFile, FiFolder, FiAward,
  FiHome, FiMenu, FiX, FiChevronDown, FiChevronRight,
  FiBell, FiSearch, FiMoreVertical, FiActivity,
  FiShoppingCart, FiLock, FiTrendingDown, FiLogOut,
  FiMail, FiPhone, FiMapPin, FiSun, FiMoon, FiMaximize2
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

// Import Components
import Sidebar from './Components/Sidebar';
import TopBar from './Components/TopBar';

// Franchise Components
import FranchiseDashboard from './Franchise/Pages/FranchiseDashboard';
import AddEmployee from './Franchise/Pages/AddEmployee';
import EmployeeList from './Franchise/Pages/EmployeeList';
import DailyAttendance from './Franchise/Pages/DailyAttendance';
import ShiftManagement from './Franchise/Pages/ShiftManagement';
import AttendanceReports from './Franchise/Pages/AttendanceReports';
import ManualAttendance from './Franchise/Pages/ManualAttendance';
import GenerateSalary from './Franchise/Pages/GenerateSalary';
import Policies from './Franchise/Pages/Policies';
import EmployeeDocs from './Franchise/Pages/EmployeeDocs';
import Departments from './Franchise/Pages/Departments';
import Designations from './Franchise/Pages/Designations';
import RolePermissions from './Franchise/Pages/RolePermissions';
import Companies from './Franchise/Pages/Companies';
import SuperAdminLogin from './Franchise/Pages/SuperAdminLogin';
import EmployeeReport from './Franchise/Pages/EmployeeReport';
import LeaveRequest from './Franchise/Pages/LeaveRequest';
import LeaveApproval from './Franchise/Pages/LeaveApproval';
import Branch from './Franchise/Pages/Branch';
import LeaveType from './Franchise/Pages/LeaveType';
import Subscription from './Franchise/Pages/Subscription';
import WhatsAppMsg from './Franchise/Pages/WhatsAppMsg';

// Support Components
import CustomerSupport from './Support/Cutomer/CustomerSupport';
import AssignTickets from './Support/Company/AssignTickets';
import SupportTickets from './Support/employee/SupportTickets';
import EmployeePerformance from './Support/Company/EmployeePerformance';
import CustomerFeedback from './Support/Cutomer/CustomerFeedback';

// Task Management
import Tasks from './pages/Tasks';
import TaskPerformance from './pages/TaskPerformance';

import TaskForm from './pages/TaskForm';

// Project Management
import ProjectList from './ProjectManagemnet/components/ProjectList';
import AddProject from './ProjectManagemnet/components/AddProject';
import EditProject from './ProjectManagemnet/components/EditProject';
import ProjectView from './ProjectManagemnet/components/ProjectView';
import ProjectTracker from './ProjectManagemnet/components/ProjectTracker';
import EmployeeLists from './ProjectManagemnet/components/EmployeeLists';
import MilestoneList from './ProjectManagemnet/components/MilestoneList';
import Dashboard from './ProjectManagemnet/pages/Dashboard';
import ReportsPage from './ProjectManagemnet/pages/ReportsPage';
import GanttChart from './ProjectManagemnet/pages/GanttChart';
import ProjectTasksManagement from './ProjectManagemnet/components/FollowUpTasks';


// Lead Management
import DashboardLead from './LeadMng/components/DashboardLead';
import LeadProfile from './LeadMng/components/LeadProfile';
import EmployeeDashboard from './LeadMng/components/EmployeeDashboard';
import FollowUpNotificationPopup from './LeadMng/components/FollowUpNotificationPopup';
import LeadAssignmentNotificationPopup from './LeadMng/components/LeadAssignmentNotificationPopup';
import AssignmentNotificationPopup from './Components/AssignmentNotificationPopup';
import './LeadMng/utils/testNotification'; // Test helper for notifications
import { cleanupStaleNotificationsOnStartup } from './LeadMng/utils/notificationCleanup';
import { AuthProvider, useAuth } from './LeadMng/context/AuthContext';
import Register from './LeadMng/components/Register';
import './LeadMng/App.css';
import AutoCaptureForm from './LeadMng/components/AutoCaptureForm';
import DemoLogin from './LeadMng/components/DemoLogin';
import SalesTeamTest from './LeadMng/components/SalesTeamTest';
import EmployeeLogin from './LeadMng/components/EmployeeLogin';
import EnquiryFlowDashboard from './LeadMng/components/EnquiryFlowDashboard';
import ComprehensiveDashboard from './Components/DashAll/ComprehensiveDashboard';
import SaveData from './Support/Company/SaveData';

// Additional Pages
import AppraisalHistory from './Franchise/Pages/AppraisalHistory';
import LeaveReport from './Franchise/Pages/LeaveReport';
import Franchises from './Franchise/Pages/Franchises';
import HolidayCreation from './Franchise/Pages/HolidayCreation';
import SalaryAdvance from './Franchise/Pages/SalaryAdvance';
import Payslip from './Franchise/Pages/Payslip';
import DepartmentReportSummary from './Franchise/Pages/DepartmentReportSummary';
import Review from './Franchise/Pages/Review';
import LeavePolicy from './Franchise/Pages/LeavePolicy';
import GoalSetting from './Franchise/Pages/GoalSetting';
import SupDashboard from './SuperAdmin/SupDashboard';
import EmpDashboard from './SuperAdmin/EmpDashboard';
import FranchiseCompanies from './Franchise/Pages/FranchiseCompanies';
import AddSuperEmployee from './Franchise/Pages/AddSuperEmployee';
const PublicRoute = ({ children }) => {
  return children;
};

const ProtectedRoute = ({ children, allowedUserTypes }) => {
  return children;
};

// Layout Component with Full TopBar Functionality
const Layout = ({ children, pageTitle = "Dashboard" }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(pageTitle);

  // Real data state (from FranchiseDashboard)
  const [currentUser, setCurrentUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [dashboardStats, setDashboardStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Track screen size for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile && !isSidebarOpen) {
        // Auto-open sidebar when resizing to desktop
        setIsSidebarOpen(true);
      }
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  // API base URL
  const isLocal = window.location.hostname === 'localhost';
  const API_BASE_URL = isLocal ? 'https://crm-tkxn.onrender.com/api' : 'https://crm-tkxn.onrender.com/api';

  // Utility functions from FranchiseDashboard
  const getCompanyId = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        let id = null;
        if (decodedToken && decodedToken.role === 'company') {
          id = decodedToken.id;
        } else {
          id = decodedToken.companyId || decodedToken.company?._id || decodedToken.company;
        }

        // Ensure ID is a string if it's an object
        if (id && typeof id === 'object') {
          id = id._id || id.id;
        }
        if (id) {
          localStorage.setItem('selectedCompanyId', id);
          return id;
        }
      } catch (error) {
        console.error('JWT decoding failed:', error);
      }
    }

    const storedCompanyId = localStorage.getItem('selectedCompanyId') || localStorage.getItem('companyId');
    if (storedCompanyId) {
      return storedCompanyId;
    }

    return null;
  }, []);

  const getCurrentUserId = useCallback(() => {
    const possibleKeys = ['userId', 'user_id', 'id', 'employeeId', 'employee_id'];
    for (const key of possibleKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        return value.replace(/['"]/g, '').trim();
      }
    }

    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.userId || decoded.id || decoded.user?.id;
      }
    } catch (error) {
      console.error('Failed to extract userId from JWT:', error);
    }

    return null;
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const authToken = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

      if (!authToken) {
        throw new Error('No authentication token found');
      }

      const decodedToken = jwtDecode(authToken);
      const userId = getCurrentUserId();
      const companyId = await getCompanyId();

      let userData = null;

      if (decodedToken.role === 'company') {
        try {
          const response = await fetch(`${API_BASE_URL}/companies/${decodedToken.id}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            userData = await response.json();
          }
        } catch (error) {
          console.warn('Company profile fetch failed:', error);
        }
      } else if (userId && decodedToken.role !== 'company') {
        try {
          const response = await fetch(`${API_BASE_URL}/employees/${userId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            userData = await response.json();
          }
        } catch (error) {
          console.warn('Employee profile fetch failed:', error);
        }
      }

      if (!userData) {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            userData = await response.json();
          }
        } catch (error) {
          console.warn('Fallback profile fetch failed:', error);
        }
      }

      if (userData) {
        const enhancedUserData = {
          ...userData,
          id: decodedToken.id,
          userId: userId || decodedToken.id,
          role: decodedToken.role,
          companyId: companyId || decodedToken.companyId,
          accessPermissions: decodedToken.accessPermissions || [],
          tokenData: {
            iat: decodedToken.iat,
            exp: decodedToken.exp,
            isExpired: Date.now() >= decodedToken.exp * 1000
          }
        };

        setCurrentUser(enhancedUserData);
      } else {
        throw new Error('Failed to fetch user data from all endpoints');
      }

    } catch (error) {
      console.error('Error in fetchCurrentUser:', error);

      try {
        const authToken = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
        const decodedToken = authToken ? jwtDecode(authToken) : null;
        const userId = getCurrentUserId();
        const companyId = await getCompanyId();

        const fallbackUser = {
          id: decodedToken?.id || userId || 'unknown',
          userId: userId || decodedToken?.id || 'unknown',
          name: decodedToken?.role === 'company' ? 'Company Admin' : 'Super Admin',
          email: 'oneclickadmin@gmail.com',
          role: decodedToken?.role || 'Administrator',
          companyId: companyId || decodedToken?.companyId || null,
          accessPermissions: decodedToken?.accessPermissions || [],
          avatar: null,
          initials: decodedToken?.role === 'company' ? 'CA' : 'AU',
          tokenData: decodedToken ? {
            iat: decodedToken.iat,
            exp: decodedToken.exp,
            isExpired: Date.now() >= decodedToken.exp * 1000
          } : null
        };

        setCurrentUser(fallbackUser);
      } catch (fallbackError) {
        console.error('Fallback user creation failed:', fallbackError);
        setCurrentUser({
          id: 'unknown',
          name: 'User',
          email: 'user@company.com',
          role: 'User',
          avatar: null,
          initials: 'U'
        });
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      const response = await fetch(`${API_BASE_URL}/notifications`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const notificationData = await response.json();
        setNotifications(notificationData);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

      const [employeesRes, tasksRes, projectsRes, ticketsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/employees/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/tasks/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/projects/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/tickets/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const employeeData = employeesRes.ok ? await employeesRes.json() : { count: 0, change: 0 };
      const taskData = tasksRes.ok ? await tasksRes.json() : { completed: 0, change: 0 };
      const projectData = projectsRes.ok ? await projectsRes.json() : { count: 0, change: 0 };
      const ticketData = ticketsRes.ok ? await ticketsRes.json() : { count: 0, change: 0 };

      setDashboardStats([
        {
          title: "Congratulations! 🎉",
          subtitle: "Best performance this month",
          value: "$42.8k",
          percentage: "78% of target 🚀",
          bgColor: "bg-gradient-to-r from-[#7367F0] to-[#5A67D8]",
          textColor: "text-white",
          showButton: true
        },
        {
          title: "Total Employees",
          value: employeeData.count?.toString() || "0",
          period: "Active Employees",
          change: employeeData.change ? `${employeeData.change > 0 ? '+' : ''}${employeeData.change}%` : "+0%",
          changeType: employeeData.change >= 0 ? "positive" : "negative",
          icon: <FiUsers className="text-[#7367F0]" />
        },
        {
          title: "Tasks Completed",
          value: taskData.completed?.toString() || "0",
          period: "This Month",
          change: taskData.change ? `${taskData.change > 0 ? '+' : ''}${taskData.change}%` : "+0%",
          changeType: taskData.change >= 0 ? "positive" : "negative",
          icon: <FiCheckSquare className="text-[#28C76F]" />
        },
        {
          title: "Total Projects",
          value: projectData.count?.toString() || "0",
          change: projectData.change ? `${projectData.change > 0 ? '+' : ''}${projectData.change}%` : "+0%",
          changeType: projectData.change >= 0 ? "positive" : "negative",
          icon: <FiFolder className="text-[#FF4560]" />
        },
        {
          title: "Support Tickets",
          value: ticketData.count?.toString() || "0",
          change: ticketData.change ? `${ticketData.change > 0 ? '+' : ''}${ticketData.change}%` : "+0%",
          changeType: ticketData.change >= 0 ? "positive" : "negative",
          icon: <FiActivity className="text-[#00E396]" />
        }
      ]);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setDashboardStats([]);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, unread: false }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('companyId');
    localStorage.removeItem('user_id');
    localStorage.removeItem('employeeId');
    localStorage.removeItem('employee_id');
    sessionStorage.clear();

    setCurrentUser(null);

    // Wait 3 seconds then redirect and reload once
    setTimeout(() => {
      // Using replace instead of href prevents back button issues
      // The reload happens automatically when navigating to a new page
      window.location.replace('/');
    }, 1000);
  }, [navigate]);

  const validateToken = useCallback(() => {
    try {
      const authToken = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!authToken) {
        handleLogout();
        return false;
      }

      const decodedToken = jwtDecode(authToken);
      const isExpired = Date.now() >= decodedToken.exp * 1000;

      if (isExpired) {
        handleLogout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      handleLogout();
      return false;
    }
  }, [handleLogout]);

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleNavigation = (path, title) => {
    setCurrentPage(title || 'Dashboard');
    navigate(path);
    // Auto-close sidebar on mobile after navigating
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen || isNotificationDropdownOpen) {
        setIsProfileDropdownOpen(false);
        setIsNotificationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen, isNotificationDropdownOpen]);

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);

      if (!validateToken()) {
        setLoading(false);
        return;
      }

      try {
        await Promise.all([
          fetchCurrentUser(),
          fetchNotifications(),
          fetchDashboardStats()
        ]);
      } catch (error) {
        setError('Failed to load dashboard data');
        console.error('Error initializing dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();

    const tokenCheckInterval = setInterval(() => {
      validateToken();
    }, 60000);

    const dataRefreshInterval = setInterval(() => {
      fetchNotifications();
      fetchDashboardStats();
    }, 30000);

    return () => {
      clearInterval(tokenCheckInterval);
      clearInterval(dataRefreshInterval);
    };
  }, [validateToken]);

  // Update current page when location changes
  useEffect(() => {
    const pathToTitle = {
      '/comprehensivedashboard': 'Dashboard',
      '/add-employee': 'Add Employee',
      '/employee-list': 'Employee List',
      '/daily-attendance': 'Daily Attendance',
      '/manual-attendance': 'Manual Attendance',
      '/shift-management': 'Shift Management',
      '/attendance-reports': 'Attendance Reports',
      '/generate-salary': 'Generate Salary',
      '/policies': 'Policies',
      '/employee-docs': 'Employee Documents',
      '/departments': 'Departments',
      '/designations': 'Designations',
      '/role-permissions': 'Role Permissions',
      '/companies': 'Companies',
      '/employee-report': 'Employee Report',
      '/leave-request': 'Leave Request',
      '/leave-approval': 'Leave Approval',
      '/branch': 'Branch',
      '/leave-type': 'Leave Type',
      '/subscription': 'Subscription',
      '/customer-support': 'Customer Support',
      '/com-tickets': 'Company Tickets',
      '/sup-tickets': 'Support Tickets',
      '/employee-performance': 'Employee Performance',
      '/tasks': 'Tasks',
      '/taskperformance': 'Task Performance',
      '/lead-mng': 'Lead Management',
      '/dashboard-projectmgnt': 'Projects Dashboard',
      '/projects': 'Projects',
      '/projects/add': 'Add Project',
      '/employees': 'Employee Lists',
      '/kanban': 'Project Tracker',
      '/project-tasks': 'Project Tasks',
      '/reports': 'Reports',
      '/AppraisalHistory': 'Appraisal History',
      '/GoalSetting': 'Goal Setting',
      '/Review': 'Review',
      '/LeaveReport': 'Leave Report',
      '/DepartmentReportSummary': 'Department Report',
      '/franchises': 'Franchises',
      '/update_leave': 'Holiday Creation',
      // '/salaryadvance': 'Salary Advance',
      // '/payslip': 'Payslip',
      '/LeavePolicy': 'Leave Policy',
      '/whatsapp-msg': 'WhatsApp Messages'
    };

    const title = pathToTitle[location.pathname] || 'Dashboard';
    setCurrentPage(title);
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-[#F8F9FA] overflow-hidden">
      {/* Mobile sidebar overlay backdrop */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <Sidebar
        currentUser={currentUser}
        isSidebarOpen={isSidebarOpen}
        isMobile={isMobile}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        onNavigate={handleNavigation}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar
          currentPage={currentPage}
          currentUser={currentUser}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          isNotificationDropdownOpen={isNotificationDropdownOpen}
          setIsNotificationDropdownOpen={setIsNotificationDropdownOpen}
          notifications={notifications}
          onMarkNotificationAsRead={markNotificationAsRead}
          onLogout={handleLogout}
          getUserInitials={getUserInitials}
          onNavigate={handleNavigation}
          dashboardStats={dashboardStats}
        />

        <div className="flex-1 overflow-auto px-2 py-3 md:px-4 md:py-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// Individual Page Wrappers
const PageWithLayout = ({ component: Component, pageTitle, ...props }) => (
  <Layout pageTitle={pageTitle}>
    <Component {...props} />
  </Layout>
);

function App() {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [milestones, setMilestones] = useState([]);

  return (
    <AuthProvider>
      <Router>
        {/* Global Follow-up Notification Popup - Shows on all pages */}
        {/* <FollowUpNotificationPopup /> */}

        {/* Global Lead Assignment Notification Popup - Shows on all pages */}
        {/* <LeadAssignmentNotificationPopup /> */}
        {/* Unified right-side assignment popup (tasks, tickets, leads) */}
        <AssignmentNotificationPopup />

        <Routes>
          {/* Public Routes (without sidebar) */}
          <Route path="/" element={<SuperAdminLogin />} />
          <Route path="/employee-login" element={<EmployeeLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/capture" element={<AutoCaptureForm />} />

          <Route path="/franchise/:franchiseId/companies" element={<PageWithLayout component={FranchiseCompanies} pageTitle="Companies" />} />

          {/* Protected Routes with Layout */}
          <Route path="/comprehensivedashboard" element={<PageWithLayout component={ComprehensiveDashboard} pageTitle="Dashboard" />} />

          <Route path="/sup-dashboard" element={<PageWithLayout component={SupDashboard} pageTitle="Super Admin Dashboard" />} />
          <Route path="/emp-dashboard" element={<PageWithLayout component={ComprehensiveDashboard} pageTitle="Employee Dashboard" />} />


          <Route path="/super-employee" element={<PageWithLayout component={AddSuperEmployee} pageTitle="Super Employee" />} />


          {/* Employee Management */}
          <Route path="/add-employee" element={<PageWithLayout component={AddEmployee} pageTitle="Add Employee" />} />
          <Route path="/employee-list" element={<PageWithLayout component={EmployeeList} pageTitle="Employee List" />} />
          <Route path="/employee-report" element={<PageWithLayout component={EmployeeReport} pageTitle="Employee Report" />} />

          {/* Attendance */}
          <Route path="/daily-attendance" element={<PageWithLayout component={DailyAttendance} pageTitle="Daily Attendance" />} />
          <Route path="/manual-attendance" element={<PageWithLayout component={ManualAttendance} pageTitle="Manual Attendance" />} />
          <Route path="/shift-management" element={<PageWithLayout component={ShiftManagement} pageTitle="Shift Management" />} />
          <Route path="/attendance-reports" element={<PageWithLayout component={AttendanceReports} pageTitle="Attendance Reports" />} />

          {/* Leaves */}
          <Route path="/leave-request" element={<PageWithLayout component={LeaveRequest} pageTitle="Leave Request" />} />
          <Route path="/leave-approval" element={<PageWithLayout component={LeaveApproval} pageTitle="Leave Approval" />} />
          <Route path="/leave-type" element={<PageWithLayout component={LeaveType} pageTitle="Leave Type" />} />

          {/* Payroll */}
          <Route path="/generate-salary" element={<PageWithLayout component={GenerateSalary} pageTitle="Generate Salary" />} />
          {/* <Route path="/salaryadvance" element={<PageWithLayout component={SalaryAdvance} pageTitle="Salary Advance" />} />
          <Route path="/payslip" element={<PageWithLayout component={Payslip} pageTitle="Payslip" />} /> */}

          {/* Support */}
          <Route path="/customer-support" element={<PageWithLayout component={CustomerSupport} pageTitle="Customer Support" />} />
          <Route path="/com-tickets" element={<PageWithLayout component={AssignTickets} pageTitle="Company Tickets" />} />
          <Route path="/sup-tickets" element={<PageWithLayout component={SupportTickets} pageTitle="Support Tickets" />} />
          <Route path="/employee-performance" element={<PageWithLayout component={EmployeePerformance} pageTitle="Employee Performance" />} />
          <Route path="/:ticketId" element={<PageWithLayout component={CustomerFeedback} pageTitle="Customer Feedback" />} />

          {/* Task Management */}
          <Route path="/tasks" element={<PageWithLayout component={Tasks} pageTitle="Tasks" />} />
          <Route path="/taskperformance" element={<PageWithLayout component={TaskPerformance} pageTitle="Task Performance" />} />

          {/* Project Management */}
          <Route path="/dashboard-projectmgnt" element={<PageWithLayout component={Dashboard} pageTitle="Projects Dashboard" projects={projects} employees={employees} milestones={milestones} />} />
          <Route path="/projects" element={<PageWithLayout component={ProjectList} pageTitle="Projects" projects={projects} setProjects={setProjects} />} />
          <Route path="/projects/add" element={<PageWithLayout component={AddProject} pageTitle="Add Project" projects={projects} setProjects={setProjects} />} />
          <Route path="/projects/edit/:id" element={<PageWithLayout component={EditProject} pageTitle="Edit Project" projects={projects} setProjects={setProjects} />} />
          <Route path="/projects/:id" element={<PageWithLayout component={ProjectView} pageTitle="Project View" projects={projects} employees={employees} milestones={milestones} />} />
          <Route path="/employees" element={<PageWithLayout component={EmployeeLists} pageTitle="Employee Lists" />} />
          <Route path="/milestones" element={<PageWithLayout component={MilestoneList} pageTitle="Milestones" milestones={milestones} setMilestones={setMilestones} projects={projects} />} />
          <Route path="/kanban" element={<PageWithLayout component={ProjectTracker} pageTitle="Project Tracker" projects={projects} setProjects={setProjects} milestones={milestones} setMilestones={setMilestones} />} />
          <Route path="/reports" element={<PageWithLayout component={ReportsPage} pageTitle="Reports" projects={projects} employees={employees} milestones={milestones} />} />
          <Route path="/gantt" element={<PageWithLayout component={GanttChart} pageTitle="Gantt Chart" />} />
          <Route path="/project-tasks" element={<PageWithLayout component={ProjectTasksManagement} pageTitle="Project Tasks" />} />

          {/* Lead Management */}
          <Route path="/lead-mng" element={<PageWithLayout component={DashboardLead} pageTitle="Lead Management" />} />
          <Route path="/lead/:id" element={<PageWithLayout component={LeadProfile} pageTitle="Lead Profile" />} />

          {/* Performance & Reviews */}
          <Route path="/AppraisalHistory" element={<PageWithLayout component={AppraisalHistory} pageTitle="Appraisal History" />} />
          <Route path="/GoalSetting" element={<PageWithLayout component={GoalSetting} pageTitle="Goal Setting" />} />
          <Route path="/Review" element={<PageWithLayout component={Review} pageTitle="Review" />} />

          {/* Reports & Analytics */}
          <Route path="/LeaveReport" element={<PageWithLayout component={LeaveReport} pageTitle="Leave Report" />} />
          <Route path="/DepartmentReportSummary" element={<PageWithLayout component={DepartmentReportSummary} pageTitle="Department Report" />} />

          {/* Policies & Documents */}
          <Route path="/policies" element={<PageWithLayout component={Policies} pageTitle="Policies" />} />
          <Route path="/employee-docs" element={<PageWithLayout component={EmployeeDocs} pageTitle="Employee Documents" />} />
          <Route path="/LeavePolicy" element={<PageWithLayout component={LeavePolicy} pageTitle="Leave Policy" />} />

          {/* Settings */}
          <Route path="/create-department" element={<PageWithLayout component={Departments} pageTitle="Departments" />} />
          <Route path="/designations" element={<PageWithLayout component={Designations} pageTitle="Designations" />} />
          <Route path="/role-permissions" element={<PageWithLayout component={RolePermissions} pageTitle="Role Permissions" />} />
          <Route path="/companies" element={<PageWithLayout component={Companies} pageTitle="Companies" />} />
          <Route path="/franchises" element={<PageWithLayout component={Franchises} pageTitle="Franchises" />} />
          <Route path="/branch" element={<PageWithLayout component={Branch} pageTitle="Branch" />} />
          <Route path="/subscription" element={<PageWithLayout component={Subscription} pageTitle="Subscription" />} />
          <Route path="/whatsapp-msg" element={<PageWithLayout component={WhatsAppMsg} pageTitle="WhatsApp Msg" />} />
          <Route path="/update_leave" element={<PageWithLayout component={HolidayCreation} pageTitle="Holiday Creation" />} />

          {/* Other Routes */}
          <Route path="/storedata" element={<PageWithLayout component={SaveData} pageTitle="Save Data" />} />

          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/comprehensivedashboard" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;


